// BlogDatabase.ts
//
// Query helpers for the blog_posts Supabase table.
//
// Per the HTML-to-Template Protocol (Section 3.6):
//   - Use `supabase` (auth client) for dashboard writes / creator-owned mutations.
//   - Use `supabasePublic` for anonymous public page reads (session-free).
//   - Always guard on both username AND userId before fetching.
//   - Public status filter MUST be .in("status", ["active", "published"]).
//   - New writes start as `draft` until the creator Approves (then `active`).
//   - For article lookup: two sequential .eq() queries (slug → id fallback).
//     Never use .or("slug.eq.X,id.eq.X") — fails silently for hyphenated slugs.

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../../config/supabaseClient';
import { loadTemplateSettings } from './TemplateDatabase';
import type { JournalPost } from '../../Screens/DashboardView/MasterTemplateEditor/types';
import { getCreatorSpaRelativePath } from '../url/getCreatorSpaRelativePath';
import type { PersistedImageCrop } from '../imageCrop/cropStatePersist';
import { isLikelyVideoUrl, pickBestStillImageUrl } from '../media/feedMedia';

export const PUBLISHED_BLOG_STATUSES = ['active', 'published'] as const;
export const BLOG_DRAFT_STATUS = 'draft';

/** Columns that exist on all blog_posts deployments (no optional `slug` column). */
const BLOG_POST_ROW_SELECT =
  'id,title,body,topic,status,username,user_id,source_post_id,published_at,created_at,meta_description,platform';

/**
 * Media columns on per-creator `{username}` content tables.
 * Do NOT add thumbnail_url / storage_url / card_image / table_image here —
 * those exist on other tables (e.g. content_instagram) but not on `{username}`,
 * and selecting them returns PostgREST 42703 (HTTP 400) in the browser console.
 */
const USERNAME_CONTENT_MEDIA_SELECT = 'post_id, media_url, thumbnail';

export type BlogPublishStatus = 'draft' | 'active' | 'published';

export function isPublishedBlogStatus(status?: string | null): boolean {
  const s = String(status || '').trim().toLowerCase();
  return s === 'active' || s === 'published';
}

/** Awaiting creator approval — editor-only, hidden on the public site. */
export function isPendingBlogApproval(status?: string | null): boolean {
  const s = String(status || '').trim().toLowerCase();
  return s === 'draft' || s === 'pending';
}

/**
 * Editor journal order: keep the fetched date order, then place every
 * unpublished post after the published ones. Public feeds never call this.
 */
export function orderEditorJournalPosts(posts: JournalPost[]): JournalPost[] {
  return [...posts].sort((a, b) => {
    const aRank = isPublishedBlogStatus(a.status) ? 0 : 1;
    const bRank = isPublishedBlogStatus(b.status) ? 0 : 1;
    return aRank - bRank;
  });
}

// ── Row type ───────────────────────────────────────────────────
export interface BlogPostRow {
  id:               number;
  title?:           string | null;
  slug?:            string | null;
  body?:            string | null;
  topic?:           string | null;
  published_at?:    string | null;
  created_at?:      string | null;
  /** Some rows store the primary media URL on the row; otherwise see `meta_description`. */
  media_url?:       string | null;
  /** JSON object: { thumbnail?: string; media?: string; media_url?: string } */
  meta_description?: unknown;
  /** Serialized source post (often holds media_url + thumbnail when meta_description is null). */
  platform?:        unknown;
  status?:          string | null;
  username?:        string | null;
  user_id?:         string | null;
  /** ID of the source content_instagram post this blog was generated from. */
  source_post_id?:  string | null;
  /** Mirrors source_post_id on some rows (saveBlogDraft insert). */
  post_id?:         string | null;
  /** Row-level image fallbacks (varies by migration / generator). */
  cover_image?:     string | null;
  image_url?:       string | null;
  image?:           string | null;
  thumbnail?:       string | null;
  thumbnail_url?:  string | null;
}

function firstNonEmptyStr(...parts: unknown[]): string {
  for (const p of parts) {
    if (p == null) continue;
    const s = String(p).trim();
    if (s && s !== 'null' && s !== 'undefined') return s;
  }
  return '';
}

function snapshotSourceMedia(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const media_url = firstNonEmptyStr(o.media_url, o.media, o.storage_url);
  const thumbnail = pickBestStillImageUrl([
    o.thumbnail,
    o.thumbnail_url,
    o.card_image,
    o.table_image,
    o.storage_url,
    // Prefer a still from media_url when thumbnail columns are empty
    // (common for Reels-sourced auto blogs).
    o.media_url,
    o.media,
  ]);
  return {
    ...o,
    ...(media_url ? { media_url, media: media_url } : {}),
    ...(thumbnail
      ? { thumbnail, thumbnail_url: firstNonEmptyStr(o.thumbnail_url, thumbnail) }
      : {}),
  };
}

/**
 * `meta_description` is often jsonb (object) but can arrive as a JSON string, or be null.
 */
function metaDescriptionAsRecord(meta: unknown): Record<string, unknown> | null {
  if (meta == null) return null;
  if (Array.isArray(meta)) {
    const first = meta[0];
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      return first as Record<string, unknown>;
    }
    return null;
  }
  if (typeof meta === 'string') {
    const t = meta.trim();
    if (!t || t === '{}' || t === 'null') return null;
    try {
      const parsed: unknown = JSON.parse(t);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof meta === 'object' && !Array.isArray(meta)) {
    return meta as Record<string, unknown>;
  }
  return null;
}

/** True when the primary media URL is video (use still image for `<img>`). */
export function mediaUrlLooksLikeVideo(mediaUrl: string): boolean {
  return isLikelyVideoUrl(mediaUrl);
}

/** Prefer a still URL safe for `<img>` / CSS background-image. */
function stillUrlForMediaSlot(
  mediaUrl: string,
  poster: string | undefined,
): { src: string; isVideo: boolean; poster?: string } | null {
  if (mediaUrlLooksLikeVideo(mediaUrl)) {
    if (!mediaUrl) return null;
    // Keep the playable URL on `src` (CreatorFeedVideoPlayer); poster is the still.
    return {
      src: mediaUrl,
      isVideo: true,
      poster: poster || undefined,
    };
  }
  if (!mediaUrl) return null;
  return { src: mediaUrl, isVideo: false };
}

/** Comma-separated `media_url` / thumbnail lists used by carousel posts. */
export function splitCommaMediaUrls(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitCommaMediaUrls(item));
  }
  if (typeof value !== 'string') return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && part !== 'null' && part !== 'undefined');
}

export type JournalMediaSlot = {
  src: string;
  isVideo: boolean;
  poster?: string;
};

function mediaAndThumbLists(post: BlogPostRow): {
  mediaParts: string[];
  thumbParts: string[];
} {
  const m = postMediaRecord(post);
  const mediaParts = splitCommaMediaUrls(
    firstNonEmptyStr(m?.media_url, m?.media, post.media_url),
  );
  const thumbParts = splitCommaMediaUrls(
    firstNonEmptyStr(
      m?.thumbnail,
      m?.thumbnail_url,
      post.thumbnail,
      post.thumbnail_url,
    ),
  );
  return { mediaParts, thumbParts };
}

/** Every media slot from comma-separated `media_url` (videos use aligned thumbnails). */
export function getPostMediaSlots(post: BlogPostRow): JournalMediaSlot[] {
  const { mediaParts, thumbParts } = mediaAndThumbLists(post);
  const fallbackStill = firstNonEmptyStr(
    post.cover_image,
    post.image_url,
    post.image,
    post.thumbnail,
    post.thumbnail_url,
  );
  if (mediaParts.length === 0) {
    return fallbackStill ? [{ src: fallbackStill, isVideo: false }] : [];
  }
  const slots: JournalMediaSlot[] = [];
  for (let i = 0; i < mediaParts.length; i++) {
    const url = mediaParts[i];
    const poster = thumbParts[i] || thumbParts[0] || fallbackStill || undefined;
    const slot = stillUrlForMediaSlot(url, poster);
    if (slot?.src) slots.push(slot);
  }
  return slots;
}

export function getJournalPostMediaSlots(post: JournalPost): JournalMediaSlot[] {
  return getPostMediaSlots({
    id: Number(post.id) || 0,
    meta_description: post.metaDescription,
    platform: post.platform,
    image: post.image,
    cover_image: post.image,
    image_url: post.image,
    media_url: post.mediaUrl,
    thumbnail: post.image,
  } as BlogPostRow);
}

function skipLinkedContentId(id: string): boolean {
  return (
    !id ||
    id.startsWith('manual-') ||
    id.startsWith('csv-') ||
    id.startsWith('eng-') ||
    id.startsWith('roundup-')
  );
}

function linkedIdList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith('[') && text.endsWith(']')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) return linkedIdList(parsed);
      } catch {
        /* fall through */
      }
    }
    return text
      .split(/[\n,|]/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (value != null) {
    const single = String(value).trim();
    return single ? [single] : [];
  }
  return [];
}

/** Content-bank ids behind a blog: weekly `post_ids`, plus the generating post. */
export function linkedContentPostIds(post: JournalPost): string[] {
  const out: string[] = [];
  const add = (value: unknown) => {
    for (const id of linkedIdList(value)) {
      if (!skipLinkedContentId(id)) out.push(id);
    }
  };
  add(post.sourcePostId);
  for (const bag of [post.metaDescription, post.platform]) {
    const rec = metaDescriptionAsRecord(bag);
    if (!rec) continue;
    add(rec.post_ids);
    add(rec.postIds);
    add(rec.source_post_ids);
    add(rec.sourcePostIds);
    add(rec.content_post_ids);
    add(rec.contentPostIds);
    add(rec.source_post_id);
    add(rec.post_id);
  }
  return Array.from(new Set(out));
}

function slotsFromContentRow(row: Record<string, unknown>): JournalMediaSlot[] {
  const mediaParts = splitCommaMediaUrls(row.media_url);
  const thumbParts = splitCommaMediaUrls(row.thumbnail);
  if (mediaParts.length === 0) {
    const still = pickBestStillImageUrl([row.thumbnail]);
    return still ? [{ src: still, isVideo: false }] : [];
  }
  const slots: JournalMediaSlot[] = [];
  for (let i = 0; i < mediaParts.length; i++) {
    const poster = thumbParts[i] || thumbParts[0] || undefined;
    const slot = stillUrlForMediaSlot(mediaParts[i], poster);
    if (slot?.src) slots.push(slot);
  }
  return slots;
}

function dedupeMediaSlots(slots: JournalMediaSlot[]): JournalMediaSlot[] {
  const seen = new Set<string>();
  const out: JournalMediaSlot[] = [];
  for (const slot of slots) {
    const key = String(slot.src || '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(slot);
  }
  return out;
}

/** Every source image: fetched content rows first, then media stored on the blog. */
export function journalPostGallerySlots(post: JournalPost): JournalMediaSlot[] {
  return dedupeMediaSlots([
    ...(post.gallery || []),
    ...getJournalPostMediaSlots(post),
  ]);
}

/** Parse embedded post JSON from `meta_description` or `platform`. */
function postMediaRecord(post: BlogPostRow): Record<string, unknown> | null {
  return (
    metaDescriptionAsRecord(post.meta_description) ||
    metaDescriptionAsRecord(post.platform)
  );
}

/** Still image safe for `<img>` / CSS background on blog cards and article hero. */
export function getPostHeroImage(post: BlogPostRow): string {
  const m = postMediaRecord(post);
  // Never return a playable video URL — dashboard thumbs use background-image.
  return pickBestStillImageUrl([
    m?.thumbnail,
    m?.thumbnail_url,
    m?.generated_cover_image,
    m?.cover_image,
    m?.card_image,
    m?.table_image,
    m?.storage_url,
    post.thumbnail,
    post.thumbnail_url,
    post.cover_image,
    post.image_url,
    post.image,
    // Image files whose path contains "video" (Cloudinary / storage) are kept;
    // actual .mp4/.mov are skipped by pickBestStillImageUrl.
    m?.media_url,
    m?.media,
    post.media_url,
  ]);
}

/** Resolve hero image for a mapped journal card (empty → no image in UI). */
export function getJournalPostHeroImage(post: JournalPost): string {
  return getPostHeroImage({
    id: Number(post.id) || 0,
    meta_description: post.metaDescription,
    platform: post.platform,
    image: post.image,
    cover_image: post.image,
    image_url: post.image,
    media_url: post.mediaUrl,
    thumbnail: post.image,
  } as BlogPostRow);
}

// ── Helpers (put in consuming page too) ───────────────────────
export function getPostThumbnail(post: BlogPostRow): string {
  return getPostHeroImage(post);
}

export function getPostExcerpt(post: BlogPostRow, maxLength = 180): string {
  const meta = metaDescriptionAsRecord(post.meta_description);
  const fromMeta = meta?.excerpt != null ? String(meta.excerpt).trim() : '';
  // `topic` is the category label — never use it as excerpt copy.
  const src = fromMeta || stripHtmlish(String(post.body || '')).trim();
  if (!src) return '';
  return src.length > maxLength ? `${src.slice(0, maxLength)}\u2026` : src;
}

/**
 * Blog `topic` is the category label shown in journal pills.
 * Collapse whitespace so captions still fit a single-line ellipsis pill.
 */
export function normalizeBlogCategoryLabel(
  raw?: string | null,
  fallback?: string | null,
): string {
  const pick = (value: unknown): string =>
    String(value ?? "")
      .replace(/\s+/g, " ")
      .trim();
  return pick(raw) || pick(fallback);
}

function stripHtmlish(value: string): string {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\n\s*EXCERPT\s*:[\s\S]*$/i, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)([\s\S]+?)\1/g, "$2")
    .replace(/(^|\s)\*([^*\n]+)\*(?=\s|$)/g, "$1$2")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Typical adult silent reading pace used for journal / email "min read". */
const BLOG_READ_WORDS_PER_MINUTE = 200;

/** Word-based read time. Do not use raw string `.length` (that counted characters). */
export function estimateBlogReadMinutes(body: string): number {
  const plain = stripHtmlish(String(body || ""));
  const words = plain ? plain.split(/\s+/).filter(Boolean).length : 0;
  if (!words) return 0;
  return Math.max(1, Math.ceil(words / BLOG_READ_WORDS_PER_MINUTE));
}

/** Format a post's creation date for dashboard / journal display. Prefer `created_at`. */
export function getPostCreatedAt(post: BlogPostRow): string {
  const platformObj =
    post.platform && typeof post.platform === 'object'
      ? (post.platform as { created_at?: string })
      : null;
  const raw = post.created_at ?? platformObj?.created_at;
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  } catch {
    return '';
  }
}

/** @deprecated Prefer getPostCreatedAt — kept for call sites that still want publish fallback. */
export function getPostDate(post: BlogPostRow): string {
  const created = getPostCreatedAt(post);
  if (created) return created;
  const raw = post.published_at;
  if (!raw) return '';
  try {
    return new Date(raw).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export function getPostSlug(post: BlogPostRow): string {
  const rowSlug = post.slug != null ? String(post.slug).trim() : '';
  if (rowSlug) return rowSlug;
  const platform = metaDescriptionAsRecord(post.platform);
  const platformSlug = platform?.slug != null ? String(platform.slug).trim() : '';
  if (platformSlug) return platformSlug;
  // Match mapBlogRowsToJournal + sitemap `getBlogPostPublicSlug` so share /
  // preview URLs stay canonical with public journal links (`/blog/post-{id}`).
  const idStr = String(post.id ?? '').trim();
  return idStr ? `post-${idStr}` : '';
}

/** AI / cron-generated posts (not manual create, CSV import, or duplicate). */
export function isAutoGeneratedBlogPost(post: BlogPostRow): boolean {
  const src = String(post.source_post_id || '').trim();
  const postId = String(post.post_id || '').trim();
  const linkedId = src || postId;

  if (linkedId.startsWith('manual-') || linkedId.startsWith('csv-')) return false;

  const meta = metaDescriptionAsRecord(post.meta_description);
  if (meta?.kind === 'manual') return false;

  const platform = metaDescriptionAsRecord(post.platform);
  const platformKind = platform?.kind != null ? String(platform.kind).toLowerCase() : '';
  if (platformKind === 'manual' || platformKind === 'duplicate' || platformKind === 'csv_import') {
    return false;
  }
  if (platformKind === 'weekly_roundup' || platformKind === 'ai_best_post') return true;

  // Linked to Content Bank / cron source ids (instagram post_id, roundup id, etc.)
  if (linkedId) return true;

  // saveBlogDraft stores the source content row in meta_description
  if (meta) {
    const looksLikeContentPost =
      meta.post_id != null ||
      meta.caption != null ||
      (meta.type != null && meta.platform != null);
    if (looksLikeContentPost) return true;
  }

  return false;
}

function escapeEmailHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export const BLOG_EMAIL_LAYOUTS = [
  'split',
  'centered',
  'left',
  'magazine',
  'overlay',
] as const;

export type BlogEmailLayout = (typeof BLOG_EMAIL_LAYOUTS)[number];

/** Article hero controls copied into the email. Colors stay neutral. */
export type BlogEmailHeroSettings = {
  layout: BlogEmailLayout;
  showDate: boolean;
  showReadTime: boolean;
  showExcerpt: boolean;
};

export type BlogEmailCopyOptions = {
  userId?: string;
  hero?: BlogEmailHeroSettings;
  title?: string;
  excerpt?: string;
  body?: string;
};

const DEFAULT_BLOG_EMAIL_HERO: BlogEmailHeroSettings = {
  layout: 'split',
  showDate: true,
  showReadTime: true,
  showExcerpt: true,
};

let liveBlogEmailHero: BlogEmailHeroSettings | null = null;

/** Latest Post Hero controls from the website editor, including unsaved layout changes. */
export function publishBlogEmailHeroSettings(settings: BlogEmailHeroSettings): void {
  liveBlogEmailHero = settings;
}

export function readBlogEmailHeroSettings(): BlogEmailHeroSettings | null {
  return liveBlogEmailHero;
}

export function normalizeBlogEmailLayout(value: unknown): BlogEmailLayout {
  const raw = String(value || '').trim().toLowerCase();
  return (BLOG_EMAIL_LAYOUTS as readonly string[]).includes(raw)
    ? (raw as BlogEmailLayout)
    : 'split';
}

export function blogEmailHeroFromTweaks(tweaks: unknown): BlogEmailHeroSettings {
  const hero = (
    tweaks as {
      pages?: { journalPost?: { cfg?: { hero?: Record<string, unknown> } } };
    }
  )?.pages?.journalPost?.cfg?.hero;
  if (!hero || typeof hero !== 'object') return { ...DEFAULT_BLOG_EMAIL_HERO };
  return {
    layout: normalizeBlogEmailLayout(hero.style),
    showDate: hero.showDate !== false,
    showReadTime: hero.showMeta !== false,
    showExcerpt: hero.showExcerpt !== false,
  };
}

function blogEmailContentEdits(tweaks: unknown): Record<string, string> {
  const edits = (tweaks as { contentEdits?: Record<string, string> })?.contentEdits;
  return edits && typeof edits === 'object' ? edits : {};
}

/** Turn stored HTML or plain text into the paragraphs shown in the post body. */
export function blogEmailBodyParagraphs(raw: string): string[] {
  const withBreaks = String(raw || '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return withBreaks
    .split(/\n+/)
    .map((part) => part.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean);
}

function emailMediaSrc(post: BlogPostRow): { src: string; isVideo: boolean } | null {
  const slots = getPostMediaSlots(post);
  const first = slots[0];
  if (first?.isVideo) {
    const still = first.poster || getPostHeroImage(post);
    return still ? { src: still, isVideo: true } : null;
  }
  if (first?.src && !mediaUrlLooksLikeVideo(first.src)) {
    return { src: first.src, isVideo: false };
  }
  const still = getPostHeroImage(post);
  return still ? { src: still, isVideo: false } : null;
}

function emailImageTag(src: string, postUrl: string): string {
  const img = `<img src="${escapeEmailHtml(src)}" alt="" width="100%" style="display:block;width:100%;max-width:100%;height:auto;border:0;" />`;
  if (!postUrl) return img;
  return `<a href="${escapeEmailHtml(postUrl)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">${img}</a>`;
}

function emailCopyBlock(parts: {
  title: string;
  excerpt: string;
  meta: string;
  align: 'left' | 'center';
  color?: string;
}): string {
  const align = parts.align;
  const color = parts.color || '#1a1a1a';
  const muted = parts.color ? 'rgba(255,255,255,0.86)' : '#555555';
  const metaColor = parts.color ? 'rgba(255,255,255,0.72)' : '#666666';
  return [
    `<h2 style="margin:0 0 10px;font-size:26px;line-height:1.2;font-weight:700;color:${color};text-align:${align};">${parts.title}</h2>`,
    parts.excerpt
      ? `<p style="margin:0 0 10px;font-size:15px;line-height:1.5;color:${muted};text-align:${align};">${parts.excerpt}</p>`
      : '',
    parts.meta
      ? `<div style="font-size:12px;letter-spacing:0.04em;color:${metaColor};text-transform:uppercase;text-align:${align};">${parts.meta}</div>`
      : '',
  ].join('');
}

function emailBodyHtml(paragraphs: string[]): string {
  if (!paragraphs.length) return '';
  return paragraphs
    .map((paragraph, index) => {
      const lead =
        index === 0
          ? 'margin:0 0 16px;padding-left:14px;border-left:3px solid #222222;'
          : 'margin:0 0 16px;';
      return `<p style="${lead}font-size:16px;line-height:1.6;color:#222222;text-align:left;">${escapeEmailHtml(paragraph)}</p>`;
    })
    .join('');
}

/**
 * Static email snapshot of one blog post. Matches the article hero layout
 * (split, centered, left, magazine, overlay) and includes the full post body.
 * Marked like carousel embeds so the existing campaign paste path renders it.
 */
export function buildBlogPostEmailHtml(
  post: BlogPostRow,
  postUrl: string,
  options?: Partial<BlogEmailHeroSettings> & {
    title?: string;
    excerpt?: string;
    body?: string;
  },
): string {
  const hero: BlogEmailHeroSettings = {
    ...DEFAULT_BLOG_EMAIL_HERO,
    ...options,
    layout: normalizeBlogEmailLayout(options?.layout || DEFAULT_BLOG_EMAIL_HERO.layout),
  };
  const title = escapeEmailHtml(
    (options?.title ?? post.title ?? '').trim() || 'Untitled',
  );
  const bodySource = options?.body != null ? options.body : String(post.body || '');
  const paragraphs = blogEmailBodyParagraphs(bodySource);
  const excerptSource =
    options?.excerpt != null
      ? String(options.excerpt)
      : getPostExcerpt(post, 280);
  const excerpt = hero.showExcerpt
    ? escapeEmailHtml(excerptSource.replace(/\s+/g, ' ').trim())
    : '';
  const date = hero.showDate ? getPostCreatedAt(post) : '';
  const minutes = estimateBlogReadMinutes(paragraphs.join(' '));
  const meta = escapeEmailHtml(
    [
      hero.showReadTime && minutes ? `${minutes} MIN READ` : '',
      date,
    ]
      .filter(Boolean)
      .join(' · '),
  );
  const media = emailMediaSrc(post);
  const imageHtml = media ? emailImageTag(media.src, postUrl) : '';
  const copyLeft = emailCopyBlock({ title, excerpt, meta, align: 'left' });
  const copyCenter = emailCopyBlock({ title, excerpt, meta, align: 'center' });
  const bodyHtml = emailBodyHtml(paragraphs);
  const viewLink = postUrl
    ? `<p style="margin:8px 0 0;text-align:left;"><a href="${escapeEmailHtml(postUrl)}" target="_blank" rel="noopener noreferrer" style="font-size:14px;font-weight:700;color:#1a1a1a;text-decoration:none;">View post →</a></p>`
    : '';

  let heroHtml = '';
  if (hero.layout === 'centered') {
    heroHtml = `${copyCenter}${imageHtml ? `<div style="margin-top:16px;">${imageHtml}</div>` : ''}`;
  } else if (hero.layout === 'left') {
    heroHtml = `${copyLeft}${imageHtml ? `<div style="margin-top:16px;">${imageHtml}</div>` : ''}`;
  } else if (hero.layout === 'overlay' && imageHtml) {
    const overlayCopy = emailCopyBlock({
      title,
      excerpt,
      meta,
      align: 'center',
      color: '#ffffff',
    });
    heroHtml = `<div style="position:relative;">${imageHtml}<div style="margin-top:-168px;position:relative;padding:20px 16px 24px;text-align:center;background:rgba(0,0,0,0.38);">${overlayCopy}</div></div>`;
  } else if ((hero.layout === 'split' || hero.layout === 'magazine') && imageHtml) {
    const copyWidth = hero.layout === 'magazine' ? '62%' : '58%';
    const mediaWidth = hero.layout === 'magazine' ? '38%' : '42%';
    heroHtml = `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-collapse:collapse;width:100%;"><tr><td valign="middle" width="${copyWidth}" style="width:${copyWidth};padding:0 16px 0 0;vertical-align:middle;text-align:left;">${copyLeft}</td><td valign="middle" width="${mediaWidth}" style="width:${mediaWidth};vertical-align:middle;">${imageHtml}</td></tr></table>`;
  } else {
    heroHtml = `${copyLeft}${imageHtml ? `<div style="margin-top:16px;">${imageHtml}</div>` : ''}`;
  }

  return `<!-- Browsable Blog - Email Snapshot -->
<div data-sb-email-embed="true" data-blog-email-layout="${hero.layout}" contenteditable="false" style="width:100%;max-width:100%;margin:0;background:transparent;border:0;border-radius:0;padding:0;font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;">
  ${heroHtml}
  ${bodyHtml ? `<div style="margin-top:22px;">${bodyHtml}</div>` : ''}
  ${viewLink}
</div>`;
}

async function resolveBlogEmailCopy(
  post: BlogPostRow,
  options?: BlogEmailCopyOptions,
): Promise<{
  hero: BlogEmailHeroSettings;
  title?: string;
  excerpt?: string;
  body?: string;
}> {
  let hero = options?.hero || readBlogEmailHeroSettings();
  let title = options?.title;
  let excerpt = options?.excerpt;
  let body = options?.body;
  const needsSaved =
    !hero || title == null || excerpt == null || body == null;
  if (needsSaved && options?.userId) {
    const row = await loadTemplateSettings(options.userId);
    const tweaks = row?.tweaks;
    if (!hero) hero = blogEmailHeroFromTweaks(tweaks);
    const edits = blogEmailContentEdits(tweaks);
    const id = String(post.id);
    if (title == null && edits[`blogPost.${id}.title`] != null) {
      title = edits[`blogPost.${id}.title`];
    }
    if (excerpt == null && edits[`blogPost.${id}.excerpt`] != null) {
      excerpt = edits[`blogPost.${id}.excerpt`];
    }
    if (body == null && edits[`blogPost.${id}.body`] != null) {
      body = edits[`blogPost.${id}.body`];
    }
  }
  return {
    hero: hero || { ...DEFAULT_BLOG_EMAIL_HERO },
    title,
    excerpt,
    body,
  };
}

/** Copy a published post's email snapshot. Both clipboard slots keep the HTML source. */
export async function copyBlogPostEmailHtml(
  username: string,
  post: BlogPostRow,
  options?: BlogEmailCopyOptions,
): Promise<boolean> {
  const resolved = await resolveBlogEmailCopy(post, options);
  const html = buildBlogPostEmailHtml(post, getBlogPostShareUrl(username, post), {
    ...resolved.hero,
    title: resolved.title,
    excerpt: resolved.excerpt,
    body: resolved.body,
  });
  try {
    const clipboardItem = (window as Window & {
      ClipboardItem?: new (items: Record<string, Blob>) => ClipboardItem;
    }).ClipboardItem;
    if (navigator.clipboard && clipboardItem) {
      const data = new clipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([html], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([data]);
      return true;
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(html);
      return true;
    }
  } catch {
    try {
      await navigator.clipboard.writeText(html);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/** Public share URL for a blog post on the creator website. */
export function getBlogPostShareUrl(username: string, post: BlogPostRow): string {
  const slug = String(username || '').trim().toLowerCase();
  if (!slug) return '';
  const postSlug = getPostSlug(post);
  const path = getCreatorSpaRelativePath({
    username: slug,
    route: `blog/${encodeURIComponent(postSlug)}`,
    pathname: "/",
  });
  if (typeof window === 'undefined') return path;
  return `${window.location.origin.replace(/\/+$/, '')}${path}`;
}

/** Dashboard list — all statuses for the signed-in creator. */
export async function fetchAllBlogPostsForDashboard(
  userId: string,
  limit = 200,
): Promise<BlogPostRow[]> {
  const uid = userId?.trim();
  if (!uid) return [];

  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[BlogDB] fetchAllBlogPostsForDashboard error:', error.message);
    return [];
  }

  const rows = (data ?? []) as BlogPostRow[];
  return backfillBlogRowsMediaFromSourcePosts(rows, uid);
}

function pushContentSourceId(ids: string[], value: unknown) {
  if (Array.isArray(value)) {
    for (const item of value) pushContentSourceId(ids, item);
    return;
  }
  const id = String(value ?? '').trim();
  if (
    !id ||
    id.startsWith('manual-') ||
    id.startsWith('csv-') ||
    id.startsWith('eng-')
  ) {
    return;
  }
  ids.push(id);
}

/** Real content-bank ids behind a draft, including engineering multi-post drafts. */
export function blogContentSourceIds(post: BlogPostRow): string[] {
  const ids: string[] = [];
  const meta = metaDescriptionAsRecord(post.meta_description);
  const platform = metaDescriptionAsRecord(post.platform);
  pushContentSourceId(ids, meta?.source_post_ids);
  pushContentSourceId(ids, meta?.post_ids);
  pushContentSourceId(ids, meta?.source_post_id);
  pushContentSourceId(ids, meta?.post_id);
  pushContentSourceId(ids, platform?.post_ids);
  pushContentSourceId(ids, platform?.source_post_ids);
  pushContentSourceId(ids, post.source_post_id);
  pushContentSourceId(ids, post.post_id);
  return Array.from(new Set(ids));
}

function bodyUsesVideoImage(body: string): boolean {
  return /!\[[^\]]*\]\([^)]*\.(?:mp4|mov|webm|m3u8|m4v)(?:[?#)\s]|$)/i.test(body);
}

/**
 * Auto-generated drafts often store only a video `media_url` in meta — pull a
 * still from the source content row so Blog dashboard thumbs aren't grey boxes.
 */
async function backfillBlogRowsMediaFromSourcePosts(
  posts: BlogPostRow[],
  userId: string,
): Promise<BlogPostRow[]> {
  const uid = userId.trim();
  const missing = posts.filter((p) => {
    const ids = blogContentSourceIds(p);
    if (!ids.length) return false;
    return !getPostHeroImage(p) || bodyUsesVideoImage(String(p.body || ''));
  });
  if (!uid || missing.length === 0) return posts;

  const uname = String(
    missing.find((p) => p.username)?.username || posts.find((p) => p.username)?.username || '',
  )
    .trim()
    .toLowerCase();
  if (!uname) return posts;

  const ids = Array.from(new Set(missing.flatMap((p) => blogContentSourceIds(p))));
  if (!ids.length) return posts;

  const { data, error } = await supabase
    .from(uname)
    .select(USERNAME_CONTENT_MEDIA_SELECT)
    .eq('user_id', uid)
    .in('post_id', ids);
  if (error || !Array.isArray(data) || data.length === 0) return posts;

  const byId = new Map<string, Record<string, unknown>>();
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const id = String(rec.post_id ?? '').trim();
    if (id) byId.set(id, rec);
  }
  if (!byId.size) return posts;

  return posts.map((p) => {
    const rows = blogContentSourceIds(p)
      .map((id) => byId.get(id))
      .filter((row): row is Record<string, unknown> => Boolean(row));
    if (!rows.length) return p;

    const stills: string[] = [];
    let nextBody = String(p.body || '');
    for (const row of rows) {
      const still = pickBestStillImageUrl([row.thumbnail, row.media_url]);
      if (still) stills.push(still);
      if (!still) continue;
      for (const part of String(row.media_url || '').split(',')) {
        const video = part.trim();
        if (video && isLikelyVideoUrl(video) && nextBody.includes(video)) {
          nextBody = nextBody.split(video).join(still);
        }
      }
    }

    const still = stills[0] || '';
    const cover = getPostHeroImage(p) || still;
    if (!cover && nextBody === String(p.body || '')) return p;
    const prevMeta = metaDescriptionAsRecord(p.meta_description) || {};
    return {
      ...p,
      body: nextBody,
      ...(cover
        ? {
            thumbnail: cover,
            thumbnail_url: cover,
            cover_image: cover,
            image: cover,
          }
        : {}),
      meta_description: cover
        ? {
            ...prevMeta,
            thumbnail: cover,
            thumbnail_url: cover,
            cover_image: cover,
            media_url: cover,
            media: cover,
          }
        : prevMeta,
    };
  });
}

/** Load one post for dashboard editor (any status). */
export async function fetchBlogPostById(
  postId: string | number,
  userId: string,
): Promise<BlogPostRow | null> {
  const uid = userId.trim();
  const id = String(postId).trim();
  if (!uid || !id) return null;

  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .maybeSingle();

  if (error) {
    console.warn('[BlogDB] fetchBlogPostById error:', error.message);
    return null;
  }
  if (!data) return null;
  const [row] = await backfillBlogRowsMediaFromSourcePosts(
    [data as BlogPostRow],
    uid,
  );
  return row ?? (data as BlogPostRow);
}

/** Map `blog_posts` rows into website-template `journal[]` cards. */
export function mapBlogRowsToJournal(
  blogData: Array<BlogPostRow | Record<string, unknown>>,
  displayName = '',
): JournalPost[] {
  return blogData
    .filter((p) => {
      const title = String(p.title || '').trim();
      const body = String(p.body || '').trim();
      return title.length > 0 || body.length > 0;
    })
    .map((p, i): JournalPost => {
      const idStr = String(p.id ?? i);
      const rawSlug =
        p.slug != null
          ? String(p.slug).trim()
          : metaDescriptionAsRecord((p as BlogPostRow).platform)?.slug != null
            ? String(metaDescriptionAsRecord((p as BlogPostRow).platform)?.slug).trim()
            : '';
      const slug = rawSlug || `post-${idStr}`;

      const image = getPostHeroImage({
        ...(p as Record<string, unknown>),
        id: Number(p.id) || 0,
      } as BlogPostRow);

      const fullBody = String(p.body ?? '');
      const meta = metaDescriptionAsRecord((p as BlogPostRow).meta_description);
      const fromMeta =
        meta?.excerpt != null ? String(meta.excerpt).trim() : '';
      const excerptSrc =
        fromMeta ||
        stripHtmlish(fullBody) ||
        String((p as Record<string, unknown>).excerpt || '').trim();
      const excerpt =
        excerptSrc.length > 220
          ? `${excerptSrc.slice(0, 220)}\u2026`
          : excerptSrc;
      const category = normalizeBlogCategoryLabel(
        p.topic,
        (p as Record<string, unknown>).category as string | null | undefined,
      );

      const platformObj =
        (p as Record<string, unknown>).platform &&
        typeof (p as Record<string, unknown>).platform === 'object'
          ? ((p as Record<string, unknown>).platform as {
              created_at?: string;
            })
          : null;
      const dateRaw = String(p.created_at || platformObj?.created_at || '');
      const date = dateRaw
        ? new Date(dateRaw).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '';

      return {
        id: idStr,
        slug,
        title: String(p.title || (fullBody.split('\n')[0]?.slice(0, 90)) || ''),
        excerpt,
        body: fullBody,
        category,
        author: displayName,
        date,
        minutes: estimateBlogReadMinutes(fullBody) || 1,
        image,
        mediaUrl: firstNonEmptyStr(
          (p as Record<string, unknown>).media_url,
          postMediaRecord(p as BlogPostRow)?.media_url,
          postMediaRecord(p as BlogPostRow)?.media,
        ) || undefined,
        sourcePostId:
          (p as Record<string, unknown>).source_post_id != null
            ? String((p as Record<string, unknown>).source_post_id ?? "").trim() || null
            : null,
        platform: (p as Record<string, unknown>).platform,
        metaDescription:
          (p as Record<string, unknown>).meta_description ??
          (p as Record<string, unknown>).platform,
        status: p.status != null ? String(p.status) : null,
      };
    });
}

/** Pull stills and carousel frames from every content post linked to the article. */
async function attachJournalContentGalleries(
  username: string,
  userId: string,
  posts: JournalPost[],
  client: SupabaseClient,
): Promise<JournalPost[]> {
  const uname = username.trim().toLowerCase();
  if (!uname || !userId) return posts;

  const ids = Array.from(
    new Set(posts.flatMap((post) => linkedContentPostIds(post))),
  );
  if (ids.length === 0) return posts;

  const byId = new Map<string, Record<string, unknown>>();
  for (let i = 0; i < ids.length; i += 80) {
    const slice = ids.slice(i, i + 80);
    const { data, error } = await client
      .from(uname)
      .select(USERNAME_CONTENT_MEDIA_SELECT)
      .eq('user_id', userId)
      .in('post_id', slice);
    if (error || !Array.isArray(data)) continue;
    for (const row of data) {
      if (!row || typeof row !== 'object') continue;
      const rec = row as Record<string, unknown>;
      const id = String(rec.post_id ?? '').trim();
      if (id) byId.set(id, rec);
    }
  }
  if (!byId.size) return posts;

  return posts.map((post) => {
    const slots = dedupeMediaSlots(
      linkedContentPostIds(post).flatMap((id) => {
        const row = byId.get(id);
        return row ? slotsFromContentRow(row) : [];
      }),
    );
    let body = String(post.body || '');
    for (const id of linkedContentPostIds(post)) {
      const row = byId.get(id);
      if (!row) continue;
      const still = pickBestStillImageUrl([row.thumbnail, row.media_url]);
      if (!still) continue;
      for (const part of String(row.media_url || '').split(',')) {
        const video = part.trim();
        if (video && isLikelyVideoUrl(video) && body.includes(video)) {
          body = body.split(video).join(still);
        }
      }
    }
    if (!slots.length && body === String(post.body || '')) return post;
    const firstStill =
      slots
        .map((slot) =>
          slot.isVideo
            ? pickBestStillImageUrl([slot.poster])
            : pickBestStillImageUrl([slot.src, slot.poster]),
        )
        .find(Boolean) || '';
    return {
      ...post,
      body,
      gallery: slots.length ? slots : post.gallery,
      image: post.image || firstStill,
    };
  });
}

/**
 * Fast path for Blog dashboard → Preview: load one post (incl. drafts) without
 * waiting on the full journal list + gallery attach for every row.
 */
export async function fetchJournalPostPreview(
  username: string,
  userId: string,
  slug: string,
  displayName: string,
  options?: { includeDrafts?: boolean; client?: SupabaseClient },
): Promise<JournalPost | null> {
  const uname = username?.trim().toLowerCase();
  const uid = userId?.trim();
  const slugParam = slug?.trim();
  if (!uname || !uid || !slugParam) return null;

  const row = await fetchBlogPost(uname, uid, slugParam, {
    publishedOnly: options?.includeDrafts === false,
  });
  if (!row) return null;

  const mapped = mapBlogRowsToJournal([row], displayName);
  if (!mapped.length) return null;

  const client = options?.client ?? supabase;
  const withGalleries = await attachJournalContentGalleries(
    uname,
    uid,
    mapped,
    client,
  );
  return withGalleries[0] || mapped[0] || null;
}

/** Website template journal feed — `blog_posts` is the source of truth. */
export async function fetchJournalForCreator(
  username: string,
  userId: string,
  displayName: string,
  options?: { includeDrafts?: boolean; limit?: number; client?: SupabaseClient },
): Promise<JournalPost[]> {
  const uid = userId?.trim();
  if (!uid) return [];

  const client = options?.client ?? supabase;
  const statuses = options?.includeDrafts
    ? [...PUBLISHED_BLOG_STATUSES, BLOG_DRAFT_STATUS, 'pending']
    : [...PUBLISHED_BLOG_STATUSES];

  // Match on user_id only. Filtering by username dropped posts whose
  // `blog_posts.username` didn't match (casing, rename, or null) so the
  // public nav hid Blog after load even when published rows existed.
  const { data, error } = await client
    .from('blog_posts')
    .select('*')
    .eq('user_id', uid)
    .in('status', statuses)
    .order('created_at', { ascending: false })
    .limit(options?.limit ?? 100);

  if (error) {
    console.warn('[BlogDB] fetchJournalForCreator error:', error.message);
    return [];
  }

  const mapped = mapBlogRowsToJournal((data ?? []) as BlogPostRow[], displayName);
  const withSourceMedia = await attachJournalContentGalleries(
    username,
    uid,
    mapped,
    client,
  );
  if (!options?.includeDrafts) return withSourceMedia;
  return orderEditorJournalPosts(withSourceMedia);
}

// ── Fetch blog list ────────────────────────────────────────────
export async function fetchBlogPosts(
  username: string,
  userId: string,
  limit = 20,
): Promise<BlogPostRow[]> {
  if (!username || !userId) return [];

  const { data, error } = await supabase
    .from('blog_posts')
    .select('id,title,body,topic,published_at,created_at,meta_description,status,platform')
    .eq('username', username)
    .eq('user_id', userId)
    .in('status', [...PUBLISHED_BLOG_STATUSES])
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[BlogDB] fetchBlogPosts error:', error.message);
    return [];
  }

  return (data ?? []) as BlogPostRow[];
}

// ── Get blog post linked to a content post ─────────────────────
// Returns the first draft/published blog that was generated from the given
// content_instagram post_id (stored in source_post_id column).
export async function getBlogForPost(
  sourcePostId: string,
): Promise<{ data: BlogPostRow | null; error: { message: string } | null }> {
  if (!sourcePostId) return { data: null, error: null };

  const { data, error } = await supabase
    .from('blog_posts')
    .select(
      'id,title,body,topic,status,username,user_id,source_post_id,created_at,meta_description,platform',
    )
    .eq('source_post_id', sourcePostId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn('[BlogDB] getBlogForPost error:', error.message);
    return { data: null, error };
  }
  return { data: data as BlogPostRow | null, error: null };
}

// ── Save / upsert a blog draft generated from a content post ───
// Does a manual check-then-update-or-insert to avoid relying on a unique
// constraint on source_post_id (which may not exist in all environments).
export async function saveBlogDraft(
  username:     string,
  userId:       string,
  topic:        string | null,
  title:        string,
  body:         string,
  sourcePostId: string,
  platform: any,

  _sourcePost?: any,
  // ?: unknown, // reserved for future metadata enrichment
): Promise<{ data: BlogPostRow | null; error: { message: string } | null }> {
  if (!username || !userId || !sourcePostId) {
    return { data: null, error: { message: 'Missing required fields' } };
  }

  // Each generate is its own draft. An older blog for this content stays as it is.
  const mediaMeta =
    snapshotSourceMedia(_sourcePost) || snapshotSourceMedia(platform);
  const blogKey = `${sourcePostId}-${Date.now()}`;
  const { data, error } = await supabase
    .from('blog_posts')
    .insert({
      username,
      user_id:        userId,
      title,
      body,
      topic,
      post_id: blogKey,
      platform: platform,
      source_post_id: sourcePostId,
      status:         BLOG_DRAFT_STATUS,
      meta_description: mediaMeta || _sourcePost || null,
    })
    .select('id,title,body,topic,status,username,user_id,source_post_id,created_at,published_at,meta_description,platform')
    .maybeSingle();

  if (error) {
    console.warn('[BlogDB] saveBlogDraft insert error:', error.message);
    if (/duplicate|unique/i.test(error.message || '')) {
      const retryKey = `${blogKey}-${Math.floor(Math.random() * 1000)}`;
      const retry = await supabase
        .from('blog_posts')
        .insert({
          username,
          user_id: userId,
          title,
          body,
          topic,
          post_id: retryKey,
          platform,
          source_post_id: retryKey,
          status: BLOG_DRAFT_STATUS,
          meta_description: mediaMeta || _sourcePost || null,
        })
        .select('id,title,body,topic,status,username,user_id,source_post_id,created_at,published_at,meta_description,platform')
        .maybeSingle();
      if (retry.data) return { data: retry.data as BlogPostRow, error: null };
      if (retry.error) return { data: null, error: retry.error };
    }
    return { data: null, error };
  }
  if (!data) {
    return { data: null, error: { message: 'Draft was not saved. Try again.' } };
  }
  return { data: data as BlogPostRow, error: null };
}

export type ManualBlogPostInput = {
  title: string;
  body: string;
  topic?: string | null;
  slug?: string | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  /** Uncropped source URL so Position image can reopen at the prior crop. */
  originalMediaUrl?: string | null;
  /** Persisted zoom/pan from ImageCropperModal. */
  imageCrop?: PersistedImageCrop | null;
};

function slugifyBlogTitle(title: string): string {
  return (
    title
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 120) || `post-${Date.now()}`
  );
}

function buildManualBlogMeta(args: {
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  originalMediaUrl?: string | null;
  imageCrop?: PersistedImageCrop | null;
}): Record<string, unknown> {
  const mediaUrl = args.mediaUrl?.trim() || '';
  const thumbnailUrl = args.thumbnailUrl?.trim() || '';
  const originalMediaUrl = args.originalMediaUrl?.trim() || '';
  const meta: Record<string, unknown> = { kind: 'manual' };
  if (mediaUrl) {
    meta.media_url = mediaUrl;
    meta.media = mediaUrl;
  }
  const thumb = thumbnailUrl || mediaUrl;
  if (thumb) {
    meta.thumbnail = thumb;
    meta.thumbnail_url = thumb;
  }
  if (originalMediaUrl) {
    meta.original_media_url = originalMediaUrl;
    meta.original_image_url = originalMediaUrl;
  }
  if (args.imageCrop) {
    meta.image_crop = args.imageCrop;
  }
  return meta;
}

/** Manual blog row for authoring in the website editor. */
export async function createBlogPostFromScratch(
  username: string,
  userId: string,
  input: ManualBlogPostInput,
): Promise<{ data: BlogPostRow | null; error: { message: string } | null }> {
  const uname = username.trim().toLowerCase();
  const uid = userId.trim();
  const title = input.title.trim();
  const body = input.body.trim();
  if (!uname || !uid) {
    return { data: null, error: { message: 'Missing required fields' } };
  }
  if (!title || !body) {
    return { data: null, error: { message: 'Title and body are required' } };
  }

  const topic = input.topic?.trim() || null;
  const slug = input.slug?.trim() || slugifyBlogTitle(title);
  const mediaUrl = input.mediaUrl?.trim() || null;
  const thumbnailUrl = input.thumbnailUrl?.trim() || null;
  const originalMediaUrl = input.originalMediaUrl?.trim() || null;
  const sourcePostId = `manual-${uid}-${Date.now()}`;
  const metaDescription = buildManualBlogMeta({
    mediaUrl,
    thumbnailUrl,
    originalMediaUrl,
    imageCrop: input.imageCrop ?? null,
  });

  const platformPayload = {
    kind: 'manual',
    slug,
    created_at: new Date().toISOString(),
    ...(mediaUrl ? { media_url: mediaUrl } : {}),
  };

  const { data, error } = await supabase
    .from('blog_posts')
    .insert({
      username: uname,
      user_id: uid,
      title,
      body,
      topic,
      post_id: sourcePostId,
      platform: platformPayload,
      source_post_id: sourcePostId,
      status: BLOG_DRAFT_STATUS,
      meta_description: metaDescription,
    })
    // Same select shape as saveBlogDraft / CSV import (proven insert path).
    .select('id,title,body,topic,status,username,user_id,source_post_id,created_at,published_at,meta_description,platform')
    .maybeSingle();

  if (error) {
    console.warn('[BlogDB] createBlogPostFromScratch error:', error.message, error);
    return { data: null, error };
  }
  if (!data) {
    return { data: null, error: { message: 'Post was not created.' } };
  }
  const row: BlogPostRow = {
    ...(data as BlogPostRow),
    slug,
    meta_description: (data as BlogPostRow).meta_description ?? metaDescription,
    platform: (data as BlogPostRow).platform ?? platformPayload,
    created_at:
      (data as BlogPostRow).created_at || platformPayload.created_at,
    ...(mediaUrl ? { media_url: mediaUrl } : {}),
  };
  return { data: row, error: null };
}

export async function setBlogPostStatus(
  postId: string | number,
  userId: string,
  status: 'draft' | 'active',
): Promise<{ data: BlogPostRow | null; error: { message: string } | null }> {
  const uid = userId.trim();
  const id = String(postId).trim();
  if (!uid || !id) {
    return { data: null, error: { message: 'Missing required fields' } };
  }

  const patch: Record<string, unknown> = { status };
  if (status === 'active') {
    patch.published_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .update(patch)
    .eq('id', id)
    .eq('user_id', uid)
    .select(BLOG_POST_ROW_SELECT)
    .maybeSingle();

  if (error) {
    console.warn('[BlogDB] setBlogPostStatus error:', error.message);
    return { data: null, error };
  }
  return { data: data as BlogPostRow | null, error: null };
}

/** Creator approval — post becomes visible on the public journal. */
export function approveBlogPost(postId: string | number, userId: string) {
  return setBlogPostStatus(postId, userId, 'active');
}

/** Pull a live post back into the editor-only approval queue. */
export function unpublishBlogPost(postId: string | number, userId: string) {
  return setBlogPostStatus(postId, userId, 'draft');
}

export type BlogPostUpdateInput = {
  title?: string;
  body?: string;
  topic?: string | null;
  excerpt?: string | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  originalMediaUrl?: string | null;
  imageCrop?: PersistedImageCrop | null;
};

/** Update an existing blog row from the dashboard editor. */
export async function updateBlogPostFields(
  postId: string | number,
  userId: string,
  input: BlogPostUpdateInput,
): Promise<{ data: BlogPostRow | null; error: { message: string } | null }> {
  const uid = userId.trim();
  const id = String(postId).trim();
  if (!uid || !id) {
    return { data: null, error: { message: 'Missing required fields' } };
  }

  const patch: Record<string, unknown> = {};
  if (input.title != null) patch.title = input.title.trim();
  if (input.body != null) patch.body = input.body;
  if (input.topic !== undefined) patch.topic = input.topic?.trim() || null;

  const touchesMeta =
    input.excerpt !== undefined ||
    input.mediaUrl !== undefined ||
    input.thumbnailUrl !== undefined ||
    input.originalMediaUrl !== undefined ||
    input.imageCrop !== undefined;

  if (touchesMeta) {
    const { data: existing } = await supabase
      .from('blog_posts')
      .select('meta_description')
      .eq('id', id)
      .eq('user_id', uid)
      .maybeSingle();
    const prevMeta =
      metaDescriptionAsRecord((existing as { meta_description?: unknown } | null)?.meta_description) ||
      {};
    const nextMeta: Record<string, unknown> = { ...prevMeta };
    if (input.excerpt !== undefined) {
      if (input.excerpt?.trim()) nextMeta.excerpt = input.excerpt.trim();
      else delete nextMeta.excerpt;
    }
    if (input.mediaUrl !== undefined || input.thumbnailUrl !== undefined) {
      const mediaUrl = input.mediaUrl?.trim() || '';
      const thumbnailUrl = input.thumbnailUrl?.trim() || '';
      if (mediaUrl) {
        nextMeta.media_url = mediaUrl;
        nextMeta.media = mediaUrl;
        const thumb = thumbnailUrl || mediaUrl;
        nextMeta.thumbnail = thumb;
        nextMeta.thumbnail_url = thumb;
      } else {
        delete nextMeta.media_url;
        delete nextMeta.media;
        delete nextMeta.thumbnail;
        delete nextMeta.thumbnail_url;
      }
    }
    if (input.originalMediaUrl !== undefined) {
      const original = input.originalMediaUrl?.trim() || '';
      if (original) {
        nextMeta.original_media_url = original;
        nextMeta.original_image_url = original;
      } else {
        delete nextMeta.original_media_url;
        delete nextMeta.original_image_url;
      }
    }
    if (input.imageCrop !== undefined) {
      if (input.imageCrop) nextMeta.image_crop = input.imageCrop;
      else delete nextMeta.image_crop;
    }
    patch.meta_description = nextMeta;
  }

  if (!Object.keys(patch).length) {
    return { data: null, error: { message: 'Nothing to update.' } };
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .update(patch)
    .eq('id', id)
    .eq('user_id', uid)
    .select(BLOG_POST_ROW_SELECT)
    .maybeSingle();

  if (error) {
    console.warn('[BlogDB] updateBlogPostFields error:', error.message);
    return { data: null, error };
  }
  return { data: data as BlogPostRow | null, error: null };
}

/** Permanently remove a blog post (dashboard admin). */
export async function deleteBlogPost(
  postId: string | number,
  userId: string,
): Promise<{ error: { message: string } | null }> {
  const uid = userId.trim();
  const id = String(postId).trim();
  if (!uid || !id) {
    return { error: { message: 'Missing required fields' } };
  }
  const { error } = await supabase
    .from('blog_posts')
    .delete()
    .eq('id', id)
    .eq('user_id', uid);
  if (error) {
    console.warn('[BlogDB] deleteBlogPost error:', error.message);
    return { error };
  }
  return { error: null };
}

/** Clone a post as a new draft. */
export async function duplicateBlogPost(
  postId: string | number,
  userId: string,
  username: string,
): Promise<{ data: BlogPostRow | null; error: { message: string } | null }> {
  const uid = userId.trim();
  const uname = username.trim().toLowerCase();
  const id = String(postId).trim();
  if (!uid || !uname || !id) {
    return { data: null, error: { message: 'Missing required fields' } };
  }

  const { data: source, error: fetchErr } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .maybeSingle();

  if (fetchErr || !source) {
    return { data: null, error: fetchErr || { message: 'Post not found.' } };
  }

  const row = source as BlogPostRow;
  const copyTitle = `${String(row.title || 'Untitled').trim()} (copy)`.slice(0, 200);
  const sourcePostId = `manual-copy-${uid}-${Date.now()}`;
  const slug = `${slugifyBlogTitle(copyTitle)}-${Date.now().toString(36).slice(-4)}`;

  const { data, error } = await supabase
    .from('blog_posts')
    .insert({
      username: uname,
      user_id: uid,
      title: copyTitle,
      body: row.body || '',
      topic: row.topic || null,
      post_id: sourcePostId,
      source_post_id: sourcePostId,
      status: BLOG_DRAFT_STATUS,
      meta_description: row.meta_description ?? null,
      platform: { kind: 'duplicate', copied_from: id, slug },
    })
    .select(BLOG_POST_ROW_SELECT)
    .maybeSingle();

  if (error) {
    console.warn('[BlogDB] duplicateBlogPost error:', error.message);
    return { data: null, error };
  }
  return { data: data as BlogPostRow | null, error: null };
}

// ── Fetch single blog post (id lookup, then platform.slug scan) ─
// Per protocol: NEVER use .or("slug.eq.X,id.eq.X") — fails for hyphens.
// Many deployments have no `slug` column; slug lives on `platform.slug` when set.
export async function fetchBlogPost(
  username: string,
  userId: string,
  slug: string,
  options?: { publishedOnly?: boolean },
): Promise<BlogPostRow | null> {
  if (!username || !userId || !slug) return null;

  const publishedOnly = options?.publishedOnly !== false;
  const slugParam = slug.trim();

  const acceptRow = (row: BlogPostRow | null): BlogPostRow | null => {
    if (!row) return null;
    if (publishedOnly && !isPublishedBlogStatus(row.status)) return null;
    return row;
  };

  // Step 1: slug param may be a stringified numeric id or `post-{id}`
  const idFromParam =
    /^\d+$/.test(slugParam)
      ? slugParam
      : /^post-(\d+)$/i.exec(slugParam)?.[1] || "";
  if (idFromParam) {
    const { data: byId, error: idErr } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('username', username)
      .eq('user_id', userId)
      .eq('id', idFromParam)
      .maybeSingle();

    if (!idErr && byId) {
      return acceptRow(byId as BlogPostRow);
    }
  }

  // Step 2: direct platform.slug match (avoids 200-row window misses)
  const { data: byPlatformSlug, error: platformErr } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('username', username)
    .eq('user_id', userId)
    .filter('platform->>slug', 'eq', slugParam)
    .maybeSingle();
  if (!platformErr && byPlatformSlug) {
    return acceptRow(byPlatformSlug as BlogPostRow);
  }

  // Step 3: paginate until match (slug may live only on platform JSON)
  const pageSize = 200;
  for (let from = 0; from < 2000; from += pageSize) {
    const { data: rows, error: listErr } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('username', username)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(from, from + pageSize - 1);

    if (listErr || !rows?.length) return null;

    const match = (rows as BlogPostRow[]).find(
      (row) => getPostSlug(row) === slugParam,
    );
    if (match) return acceptRow(match);

    if (rows.length < pageSize) break;
  }
  return null;
}
